import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ETypeOfNotification, setNotification } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { getClosingDetails } from '../../services/ProfessionalSettlementController';
import { parseApiError } from '../../services/apiError';
import { ProfessionalSettlementDTO, ProfessionalSettlementDetailItemDTO } from '../../dto/ProfessionalSettlementDTO';
import ReadonlyField from '../../components/ReadonlyField/ReadonlyField';
import { formatMoney, sumMoney } from '../../utils/money';
import { formatDate, formatDateTime } from './settlementFormat';

interface SettlementDetailProps {
    settlement: ProfessionalSettlementDTO;
    onBack: () => void;
}

/** Vista de detalle (solo lectura, DS §3.7) de un cierre de rendición, con sus ítems. */
export default function SettlementDetail({ settlement, onBack }: SettlementDetailProps) {
    const dispatch = useDispatch();
    const [items, setItems] = useState<ProfessionalSettlementDetailItemDTO[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getClosingDetails(settlement.professionalsettlement)
            .then(data => {
                if (!cancelled) setItems(data ?? []);
            })
            .catch(error => {
                if (cancelled) return;
                parseApiError(error);
                dispatch(setNotification({
                    title: 'No se pudo cargar el detalle. Intentá de nuevo.',
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [dispatch, settlement.professionalsettlement]);

    const totalHonorarium = sumMoney(items.map(item => item.professionalamount));

    return (
        <div className="settlement-detail">
            <div>
                <Button
                    text
                    icon="pi pi-angle-double-left"
                    label="Volver"
                    className="md-back-link"
                    onClick={onBack}
                />
            </div>

            <div className="settlement-detail__heading">
                <h1 className="md-page-title">Detalle de rendición</h1>
                <p className="md-entity-subtitle">
                    Cierre N° {settlement.closingnumber} · {settlement.professionalname}
                </p>
            </div>

            <div className="md-readonly-grid">
                <ReadonlyField label="Entidad facturadora">{settlement.billingentityname}</ReadonlyField>
                <ReadonlyField label="Período">{formatDate(settlement.fromdate)} – {formatDate(settlement.todate)}</ReadonlyField>
                <ReadonlyField label="Fecha de cierre">{formatDateTime(settlement.closingdatetime)}</ReadonlyField>
                <ReadonlyField label="Pagado al profesional">{formatMoney(settlement.professionalamount)}</ReadonlyField>
                <ReadonlyField label="Saldo pendiente">
                    {settlement.pendingamount > 0 ? formatMoney(settlement.pendingamount) : null}
                </ReadonlyField>
            </div>

            <DataTable
                value={items}
                loading={loading}
                className="crudTable"
                emptyMessage="Sin ítems en este cierre."
            >
                <Column field="formdate" header="Fecha" body={(row: ProfessionalSettlementDetailItemDTO) => formatDate(row.formdate)} style={{ width: 110 }} />
                <Column field="formnumber" header="N° comprobante" body={(row: ProfessionalSettlementDetailItemDTO) => row.formnumber ?? '—'} style={{ width: 110 }} />
                <Column field="patientname" header="Paciente" />
                <Column field="nomenclatordescription" header="Nomenclador" body={(row: ProfessionalSettlementDetailItemDTO) => row.nomenclatordescription ?? '—'} />
                <Column field="paymentmethodname" header="Medio de pago" style={{ width: 150 }} />
                <Column field="amounttype" header="Tipo" style={{ width: 100 }} body={(row: ProfessionalSettlementDetailItemDTO) => row.amounttype ?? '—'} />
                <Column field="amount" header="Importe" body={(row: ProfessionalSettlementDetailItemDTO) => formatMoney(row.amount)} className="text-right" headerClassName="col-right" style={{ width: 130 }} />
                <Column field="honorariumpercentage" header="% honorario" body={(row: ProfessionalSettlementDetailItemDTO) => row.honorariumpercentage != null ? `${row.honorariumpercentage}%` : '—'} className="text-right" headerClassName="col-right" style={{ width: 110 }} />
                <Column field="professionalamount" header="Honorario" body={(row: ProfessionalSettlementDetailItemDTO) => formatMoney(row.professionalamount)} className="text-right settlement-amount" headerClassName="col-right" style={{ width: 130 }} />
            </DataTable>

            {items.length > 0 && (
                <p className="settlement-detail__total">
                    Total de honorarios: <strong>{formatMoney(totalHonorarium)}</strong>
                </p>
            )}
        </div>
    );
}
